import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Star, Eye, Calendar, Settings } from 'lucide-react';
import { donationService, walletService, chapterService } from '../services/firebase';
import Navbar from './Navbar';
import Footer from './Footer';
import './UserProfile.css';

interface UserStats {
  totalChaptersRead: number;
  totalReadingTime: number;
  favoriteGenre: string;
  joinDate: Date;
  currentStreak: number;
  totalDonations: number;
  walletBalance: number;
}

interface UserProfileProps {
  className?: string;
}

const UserProfile: React.FC<UserProfileProps> = ({ className = '' }) => {
  const { user, loading } = useAuth();
  const [userStats, setUserStats] = useState<UserStats>({
    totalChaptersRead: 0,
    totalReadingTime: 0,
    favoriteGenre: 'Dark Fantasy',
    joinDate: new Date(),
    currentStreak: 0,
    totalDonations: 0,
    walletBalance: 0
  });

  useEffect(() => {
    if (user && !loading) {
      loadUserStats();
    }
  }, [user, loading]);

  const loadUserStats = async () => {
    if (!user) return;

    try {
      // Load wallet balance
      const wallet = await walletService.getOrCreateWallet(user.uid);
      
      // Load purchased chapters
      const purchasedChapters = await chapterService.getPurchasedChapters(user.uid);
      
      // Load donations
      const donations = await donationService.getUserDonations(user.uid);
      const totalDonations = donations.reduce((sum, donation) => sum + donation.amount, 0);

      // Calculate actual reading progress
      const totalChaptersRead = purchasedChapters.length + 4; // 4 free chapters
      const totalReadingTime = totalChaptersRead * 15; // Assume 15 minutes per chapter
      const currentStreak = Math.min(totalChaptersRead, 30); // Mock streak based on chapters

      setUserStats({
        totalChaptersRead,
        totalReadingTime,
        favoriteGenre: 'Dark Fantasy',
        joinDate: user.createdAt || new Date(),
        currentStreak,
        totalDonations,
        walletBalance: wallet.balance
      });
    } catch (error) {
      console.error('Error loading user stats:', error);
      // Fallback to basic stats
      setUserStats({
        totalChaptersRead: 4,
        totalReadingTime: 60,
        favoriteGenre: 'Dark Fantasy',
        joinDate: user.createdAt || new Date(),
        currentStreak: 1,
        totalDonations: 0,
        walletBalance: 0
      });
    }
  };

  if (loading) {
    return (
      <div className={`user-profile ${className}`}>
        <Navbar />
        <div className="profile-container">
          <div className="loading">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`user-profile ${className}`}>
        <Navbar />
        <div className="profile-container">
          <div className="login-prompt">
            <h2>Please log in to view your profile</h2>
            <p>Sign in to track your reading progress and view your statistics.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const formatReadingTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className={`user-profile ${className}`}>
      <Navbar />
      
      <div className="profile-container">
        <motion.div
          className="profile-header"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="profile-info">
            <h1>{user.displayName || user.email}</h1>
            <p className="profile-email">{user.email}</p>
            <div className="profile-badges">
              <span className="badge premium">Premium Reader</span>
              <span className="badge supporter">Top Supporter</span>
            </div>
          </div>
        </motion.div>

        <div className="profile-content">
          <motion.div
            className="stats-grid"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="stat-card">
              <div className="stat-icon">
                <BookOpen size={24} />
              </div>
              <div className="stat-info">
                <h3>{userStats.totalChaptersRead}</h3>
                <p>Chapters Read</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Eye size={24} />
              </div>
              <div className="stat-info">
                <h3>{formatReadingTime(userStats.totalReadingTime)}</h3>
                <p>Reading Time</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Star size={24} />
              </div>
              <div className="stat-info">
                <h3>{userStats.currentStreak}</h3>
                <p>Day Streak</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Calendar size={24} />
              </div>
              <div className="stat-info">
                <h3>₹{userStats.totalDonations}</h3>
                <p>Total Donated</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Settings size={24} />
              </div>
              <div className="stat-info">
                <h3>{userStats.walletBalance.toFixed(2)}</h3>
                <p>VOS Balance</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="reading-progress"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2>Reading Progress</h2>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${(userStats.totalChaptersRead / 12) * 100}%` }}></div>
            </div>
            <p>Chapter {userStats.totalChaptersRead} of 12 completed</p>
            
            <div className="recent-chapters">
              <h3>Recently Read</h3>
              <div className="chapter-list">
                <div className="chapter-item">
                  <span className="chapter-number">4</span>
                  <div className="chapter-details">
                    <h4>Bridge Between Worlds</h4>
                    <p>Read 2 days ago</p>
                  </div>
                </div>
                <div className="chapter-item">
                  <span className="chapter-number">3</span>
                  <div className="chapter-details">
                    <h4>The Last Keeper</h4>
                    <p>Read 1 week ago</p>
                  </div>
                </div>
                <div className="chapter-item">
                  <span className="chapter-number">2</span>
                  <div className="chapter-details">
                    <h4>Echoes of Memory</h4>
                    <p>Read 2 weeks ago</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="donation-history"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h2>Support History</h2>
            <div className="donation-list">
              <div className="donation-item">
                <div className="donation-details">
                  <h4>Chapter 3 Support</h4>
                  <p>November 15, 2024</p>
                </div>
                <span className="donation-amount">₹50</span>
              </div>
              <div className="donation-item">
                <div className="donation-details">
                  <h4>Chapter 2 Support</h4>
                  <p>November 8, 2024</p>
                </div>
                <span className="donation-amount">₹100</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UserProfile;